<template>
  <div class="contents-results">
    <div v-if="loading && !request">
      <b-skeleton width="60%" class="mb-1"></b-skeleton>
      <b-skeleton width="30%" class="mb-2"></b-skeleton>
      <div class="contents-grid">
        <b-skeleton-img v-for="index in 4" :key="index" no-aspect height="260px"></b-skeleton-img>
      </div>
    </div>

    <div v-else-if="not_found" class="text-center py-5">
      <p class="avenir-medium">{{ $t('ia.contents.requestNotFound') }}</p>
      <b-button variant="outline-primary" @click="$emit('new_request')">{{ $t('ia.contents.newRequest') }}</b-button>
    </div>

    <template v-else-if="request">
      <!-- Request summary -->
      <div class="results-header">
        <p class="results-prompt avenir-medium">{{ request.prompt }}</p>
        <div v-if="request.references && request.references.length" class="results-references">
          <small class="text-muted mr-50">{{ $t('ia.contents.references') }}:</small>
          <img v-for="reference in request.references" :key="reference.uuid" :src="reference.url" :title="reference.name" alt="" />
        </div>
        <div class="d-flex align-items-center flex-wrap results-meta">
          <b-badge :variant="status_variant" class="mr-1">{{ status_label }}</b-badge>
          <small class="text-muted mr-1">{{ aspect_ratio }}</small>
          <small v-if="request.credits_estimated !== undefined && request.credits_estimated !== null" class="text-muted">
            {{ $t('ia.contents.estimatedCredits') }}: {{ request.credits_estimated }}
          </small>
        </div>
        <b-progress
          v-if="request.status === 'processing' && progress.total > 0"
          :value="progress.completed"
          :max="progress.total"
          height="6px"
          class="mt-1"
        ></b-progress>
        <b-alert :show="request.status === 'partial'" variant="warning" class="mt-1 mb-0">
          <div class="alert-body">{{ $t('ia.contents.partialStatus', { failed: progress.failed, total: progress.total }) }}</div>
        </b-alert>
        <b-alert :show="request.status === 'failed'" variant="danger" class="mt-1 mb-0">
          <div class="alert-body">{{ $t('ia.contents.failedStatus') }}</div>
        </b-alert>
      </div>

      <!-- Preparing prompts -->
      <div v-if="request.status === 'pending' && cards.length === 0" class="d-flex align-items-center py-3">
        <b-spinner small class="mr-50"></b-spinner>{{ $t('ia.contents.preparingPrompts') }}
      </div>

      <div class="contents-grid">
        <content-card
          v-for="card in cards"
          :key="card.job.uuid"
          :job="card.job"
          :dependency="card.dependency"
          :preview="card.preview"
          :aspect_ratio="card.aspect_ratio"
          @open="viewer_job = card.job"
        ></content-card>
      </div>
    </template>

    <content-viewer
      :job="viewer_job"
      :jobs="completed_jobs"
      @navigate="viewer_job = $event"
      @close="viewer_job = null"
    ></content-viewer>
  </div>
</template>

<script>
import {
  BSkeleton,
  BSkeletonImg,
  BBadge,
  BProgress,
  BAlert,
  BSpinner,
  BButton,
} from "bootstrap-vue";
import { getContentRequest } from '@/services/ai-contents';
import {
  ACTIVE_REQUEST_STATUSES,
  REQUEST_STATUS_VARIANTS,
  POLLING_INTERVAL,
  finalJobs,
  dependencyOf,
  previewAsset,
  requestAspectRatio,
  jobAspectRatio,
  requestProgress,
  applyContentEvent,
} from './contents-utils';

export default {
  name: 'ContentsResults',
  components: {
    BSkeleton,
    BSkeletonImg,
    BBadge,
    BProgress,
    BAlert,
    BSpinner,
    BButton,
    ContentCard: () => import('./ContentCard.vue'),
    ContentViewer: () => import('./ContentViewer.vue'),
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    // Detail returned when the request was created, avoids fetching it again
    initial_request: {
      type: Object,
      default: null,
    },
    // { type: 'campaign' | 'casting_call', uuid } or null for standalone BrandMeAI
    source: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      request: null,
      loading: false,
      not_found: false,
      viewer_job: null,
      polling_timer: null,
    };
  },
  created() {
    this.$root.$on('ai_content_updated', this.onContentUpdated);
    this.loadRequest();
  },
  beforeDestroy() {
    this.$root.$off('ai_content_updated', this.onContentUpdated);
    this.stopPolling();
  },
  computed: {
    ws_connected() {
      return this.$store.getters['main_store/ws_connected'];
    },
    is_active() {
      return !!this.request && ACTIVE_REQUEST_STATUSES.includes(this.request.status);
    },
    aspect_ratio() {
      return requestAspectRatio(this.request);
    },
    progress() {
      return requestProgress(this.request);
    },
    completed_jobs() {
      return finalJobs(this.request).filter((job) => job.status === 'completed');
    },
    cards() {
      return finalJobs(this.request).map((job) => ({
        job,
        dependency: dependencyOf(this.request, job),
        preview: job.asset_type === 'video' ? previewAsset(this.request, job) : null,
        aspect_ratio: jobAspectRatio(job, this.aspect_ratio),
      }));
    },
    status_label() {
      if (!this.request) return '';
      if (this.request.status === 'processing') {
        return this.$t('ia.contents.processingStatus', { completed: this.progress.completed, total: this.progress.total });
      }
      return this.$t(`ia.contents.requestStatus.${this.request.status}`);
    },
    status_variant() {
      return (this.request && REQUEST_STATUS_VARIANTS[this.request.status]) || 'light-secondary';
    },
  },
  watch: {
    uuid() {
      this.request = null;
      this.loadRequest();
    },
    is_active(active, was_active) {
      if (was_active && !active && this.request) this.$emit('finished', this.request);
      this.updatePolling();
    },
    ws_connected(connected, was_connected) {
      // Recover events that could be lost while the socket was down
      if (connected && !was_connected && this.is_active) this.fetchRequest();
      this.updatePolling();
    },
  },
  methods: {
    loadRequest() {
      this.not_found = false;
      if (this.initial_request && this.initial_request.uuid === this.uuid) {
        this.request = JSON.parse(JSON.stringify(this.initial_request));
        this.updatePolling();
        return;
      }
      this.loading = true;
      this.fetchRequest().finally(() => {
        this.loading = false;
      });
    },
    fetchRequest() {
      const uuid = this.uuid;
      return getContentRequest(uuid).then((response) => {
        if (uuid !== this.uuid) return;
        this.request = response;
        this.updatePolling();
      }).catch((error) => {
        if (uuid === this.uuid && !this.request && [403, 404].includes(error.status)) this.not_found = true;
      });
    },
    onContentUpdated(event) {
      if (!event || event.request_uuid !== this.uuid || !this.request) return;
      const was_active = this.is_active;
      const known_job = applyContentEvent(this.request, event);

      // Unknown job, or batch just finished: sync the detail (errors, final assets)
      if (!known_job || (was_active && !this.is_active)) this.fetchRequest();
    },
    updatePolling() {
      this.stopPolling();
      if (this.is_active && !this.ws_connected) {
        this.polling_timer = setInterval(this.fetchRequest, POLLING_INTERVAL);
      }
    },
    stopPolling() {
      if (this.polling_timer) clearInterval(this.polling_timer);
      this.polling_timer = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.results-header {
  margin-bottom: 1.5rem;

  .results-prompt {
    white-space: pre-wrap;
    max-height: 120px;
    overflow-y: auto;
    margin-bottom: 0.5rem;
  }
}
.results-references {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.5rem;

  img {
    width: 36px;
    height: 36px;
    border-radius: 0.35rem;
    object-fit: cover;
  }
}
.contents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  align-items: start;
}
@media (max-width: 500px) {
  .contents-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
}
</style>
